import type { SessionDetail, SessionParticipant, CardCatalog } from '../types';
import { SCORE_DIMENSIONS, TAKES_CARDS, HORIZON_LABELS, type DimLang } from '../../shared/scoreDimensions';
import { htmlToText } from './html';

/**
 * Build the plain-ASCII per-participant session report used to transpose
 * observations into external report systems (copy → paste).
 */
export function buildParticipantReport(opts: {
  session: SessionDetail['session'];
  participant: SessionParticipant;
  catalog: CardCatalog | null;
  lang: DimLang;
  t: (key: string) => string;
}): string {
  const { session, participant, catalog, lang, t } = opts;

  const formatDate = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(lang === 'fr' ? 'fr-CH' : 'en-CH', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  };

  const presenceLabel =
    participant.presence === 'present' ? t('session.presence.present')
      : participant.presence === 'absent' ? t('session.presence.absent')
      : participant.presence === 'added' ? t('session.presence.added')
      : t('session.presence.expected');

  const cardById = new Map((catalog?.cards ?? []).map((c) => [c.id, c]));
  const cardName = (topicId: number, fallback: string) =>
    cardById.get(topicId)?.name ?? fallback;

  // ── header ──
  const title = t('report.title');
  const lines: string[] = [title, '='.repeat(title.length), ''];

  const meta: [string, string][] = [
    [t('report.participant'), participant.display_name],
    [t('report.session'), `${formatDate(session.scheduled_date)}${session.title ? ` · ${session.title}` : ''}`],
    ...(session.location ? [[t('report.location'), session.location] as [string, string]] : []),
    [t('report.animator'), session.animator_name],
    [t('report.presence'), presenceLabel],
  ];
  const metaWidth = Math.max(...meta.map(([k]) => k.length));
  for (const [k, v] of meta) lines.push(`${k.padEnd(metaWidth)} : ${v}`);
  lines.push('');

  // ── cards, each with its horizon, ateliers and context ──
  lines.push(t('report.cards'));
  if (participant.picks.length === 0) {
    lines.push(`  ${t('report.noCards')}`);
  } else {
    [...participant.picks]
      .sort((a, b) => a.pick_order - b.pick_order)
      .forEach((p, i) => {
        const tags: string[] = [];
        if (p.horizon) tags.push(HORIZON_LABELS[p.horizon][lang]);
        if (p.offering_names && p.offering_names.length > 0) tags.push(`${t('report.ateliers')} ${p.offering_names.join(', ')}`);
        lines.push(`  ${i + 1}. ${cardName(p.topic_id, p.name)}${tags.length ? ` [${tags.join(' · ')}]` : ''}`);
        if (p.context && p.context.trim()) {
          for (const l of p.context.trim().split('\n')) lines.push(`     ${l}`);
        }
      });
  }
  lines.push('');

  // ── notes ──
  lines.push(t('report.notes'));
  const rawNotes = (participant.context_notes ?? '').trim();
  const notes = /<\w+[^>]*>/.test(rawNotes) ? htmlToText(rawNotes) : rawNotes;
  if (!notes) {
    lines.push(`  ${t('report.noNotes')}`);
  } else {
    for (const l of notes.split('\n')) lines.push(`  ${l}`);
  }
  lines.push('');

  // ── défi de la semaine ──
  lines.push(t('report.defi'));
  lines.push(`  ${participant.defi?.trim() || t('report.noDefi')}`);
  lines.push('');

  // ── observations ──
  lines.push(t('report.observations'));
  const rows: { label: string; value: string }[] = [];
  for (const dim of SCORE_DIMENSIONS) {
    const letter = participant[dim.field];
    const yesNo = dim.key === 'prise_cartes' && !letter && participant.takes_cards != null
      ? (participant.takes_cards ? TAKES_CARDS.yes : TAKES_CARDS.no).short[lang] : null;
    rows.push({
      label: dim.reportLabel[lang],
      value: letter ? `${letter} — ${dim.levels[letter].short[lang]}` : yesNo ?? t('report.notScored'),
    });
  }
  const labelWidth = Math.max(...rows.map((r) => r.label.length));
  for (const r of rows) {
    const dots = '.'.repeat(labelWidth - r.label.length + 3);
    lines.push(`  ${r.label} ${dots} ${r.value}`);
  }

  return lines.join('\n');
}

/** Copy text to the clipboard, with a fallback for older browser contexts. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}
